/**
 * The funnel board — `/opportunities?view=board`.
 *
 * One column per stage, **ordered by `sort_order`** (§5.1 [R3]). Column headers
 * carry the count and the summed weighted value, added exactly with `sumMoney`
 * rather than by coercing `numeric` strings to floats (§9: money is never a
 * float).
 *
 * Drag to advance. The move is optimistic — the card jumps immediately — and
 * rolls back if the server refuses, which it does for two reasons the board
 * turns into prompts rather than errors (see `useStageMove`).
 *
 * Keyboard users get the same move from a `<select>` on each card, because
 * drag-and-drop is not keyboard-operable and §9 requires that it be (WCAG AA).
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { GripVertical } from 'lucide-react';
import { useStages } from '@/lib/reference';
import { formatMoneyCompact, sumMoney } from '@/lib/format';
import { RiskBadges } from '@/components/domain/RiskBadges';
import { PriceBasisTag } from '@/components/domain/Money';
import { useStageMove } from '@/features/opportunities/useStageMove';
import { cn } from '@/lib/utils';

type Row = Record<string, unknown>;

export function FunnelBoard({ rows }: { rows: Row[] }): React.ReactElement {
  const { list: stages } = useStages();
  const mover = useStageMove();
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  /** The optimistic override: card id -> stage it was dropped into. */
  const [optimistic, setOptimistic] = useState<Record<string, string>>({});

  const byStage = useMemo(() => {
    const grouped = new Map<string, Row[]>();
    for (const stage of stages) grouped.set(stage.code, []);
    for (const row of rows) {
      const id = String(row['id']);
      const stageCode = optimistic[id] ?? String(row['stage_code']);
      const bucket = grouped.get(stageCode);
      if (bucket !== undefined) bucket.push(row);
    }
    return grouped;
  }, [rows, stages, optimistic]);

  const drop = (row: Row, stageCode: string): void => {
    if (String(row['stage_code']) === stageCode) return;
    const id = String(row['id']);
    setOptimistic((current) => ({ ...current, [id]: stageCode }));
    mover.move(row, stageCode);
    // The rollback: the server's answer arrives as an invalidation, and any
    // refusal leaves the row where it was. Clearing the override after a beat
    // means a refused move snaps back rather than lying on screen.
    window.setTimeout(() => {
      setOptimistic((current) => {
        const next = { ...current };
        delete next[id];
        return next;
      });
    }, 2500);
  };

  return (
    <>
      <div className="flex gap-3 overflow-x-auto pb-2">
        {stages.map((stage) => {
          const cards = byStage.get(stage.code) ?? [];
          const weighted = sumMoney(cards.map((row) => row['weighted_revenue'] as string));

          return (
            <section
              key={stage.code}
              onDragOver={(event) => {
                event.preventDefault();
                setOver(stage.code);
              }}
              onDragLeave={() => setOver((current) => (current === stage.code ? null : current))}
              onDrop={(event) => {
                event.preventDefault();
                setOver(null);
                const row = rows.find((candidate) => String(candidate['id']) === dragging);
                if (row !== undefined) drop(row, stage.code);
                setDragging(null);
              }}
              className={cn(
                'flex w-72 shrink-0 flex-col rounded-lg border bg-muted/30',
                over === stage.code && 'ring-2 ring-primary',
              )}
            >
              <header className="border-b px-3 py-2">
                <h2 className="text-sm font-medium" title={stage.entry_criteria ?? undefined}>
                  {stage.name}
                </h2>
                <p className="text-xs text-muted-foreground">
                  {cards.length} · {formatMoneyCompact(weighted)} weighted
                </p>
              </header>

              <ul className="flex-1 space-y-2 p-2">
                {cards.map((row) => (
                  <li key={String(row['id'])}>
                    <BoardCard
                      row={row}
                      onDragStart={() => setDragging(String(row['id']))}
                      onMoveTo={(stageCode) => drop(row, stageCode)}
                    />
                  </li>
                ))}
                {cards.length === 0 && (
                  <li className="px-1 py-4 text-center text-xs text-muted-foreground">Empty</li>
                )}
              </ul>
            </section>
          );
        })}
      </div>

      {mover.dialogs}
    </>
  );
}

function BoardCard({
  row,
  onDragStart,
  onMoveTo,
}: {
  row: Row;
  onDragStart: () => void;
  onMoveTo: (stageCode: string) => void;
}): React.ReactElement {
  const { list: stages } = useStages();
  const channel = row['price_basis'] === 'distributor_resale' || row['rep_firm_id'] !== null;

  return (
    <div draggable onDragStart={onDragStart} className="rounded-md border bg-card p-2 shadow-sm">
      <div className="flex items-start gap-1">
        <GripVertical
          className="mt-0.5 h-3.5 w-3.5 shrink-0 cursor-grab text-muted-foreground"
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          <Link
            to={`/opportunities/${String(row['id'])}`}
            className="block truncate text-sm font-medium hover:underline"
          >
            {String(row['name'])}
          </Link>
          <p className="truncate text-xs text-muted-foreground">{String(row['account_name'])}</p>

          <p className="mt-1 flex items-center gap-1.5 text-xs">
            <span className="font-medium tabular-nums">
              {formatMoneyCompact(row['annual_revenue_net'] as string)}
            </span>
            <PriceBasisTag basis={row['price_basis'] as string} />
          </p>

          <p className="mt-1 text-[11px] text-muted-foreground">
            {String(row['owner_name'] ?? 'Unassigned')} ·{' '}
            {row['days_in_current_stage'] === null ? '—' : String(row['days_in_current_stage'])}d in
            stage
            {channel && row['rep_firm_name'] !== null && row['rep_firm_name'] !== undefined
              ? ` · ${String(row['rep_firm_name'])}`
              : ''}
          </p>

          <div className="mt-1">
            <RiskBadges row={row} max={2} />
          </div>

          {/* Keyboard equivalent of the drag (§9). */}
          <label className="mt-2 block">
            <span className="sr-only">Move {String(row['opp_number'])} to stage</span>
            <select
              className="h-7 w-full rounded border border-input bg-background px-1 text-[11px]"
              value={String(row['stage_code'])}
              onChange={(event) => onMoveTo(event.target.value)}
            >
              {stages.map((stage) => (
                <option key={stage.code} value={stage.code}>
                  Move to {stage.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>
    </div>
  );
}
